import * as React from 'react';

import { styled, useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import CssBaseline from '@mui/material/CssBaseline';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';

import logo from '../vdoo.png'
import MenuIcon from '@mui/icons-material/Menu';
import Profile from '../auth/profile'
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from '@apollo/client';
import { GET_TODOS_AND_CATEGORIES } from '../apis/graphqlQueries';

import ListAltIcon from '@mui/icons-material/ListAlt';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';

import ContactSupportIcon from '@mui/icons-material/ContactSupport';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation} from 'react-router-dom';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // Only apply negative margin on desktop where drawer is persistent
    [theme.breakpoints.up('md')]: {
      marginLeft: `-${drawerWidth}px`,
      ...(open && {
        transition: theme.transitions.create('margin', {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
        marginLeft: 0,
      }),
    },
    // On mobile, no margin shift needed (drawer overlays)
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  }),
);

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  backgroundColor: '#3a1a5a', // Added dark purple background color to match login page
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    // Only shift AppBar on desktop (md and up), not on mobile where drawer is temporary
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: `${drawerWidth}px`,
      transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

export default function Navigation() {

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // On mobile, drawer starts closed; on desktop, drawer starts open
  const [open, setOpen] = React.useState(!isMobile);

  const { logout, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();

  // Get user_id for querying todos
  const user_id = user?.sub?.split("|")[1];

  // Fetch todos to count pending conversations
  const { data } = useQuery(GET_TODOS_AND_CATEGORIES, {
    variables: { user_id },
    skip: !user_id, // Skip query if user_id is not available
  });

  // Count pending conversations (contact todos where customerResolved is false)
  const pendingConversationsCount = React.useMemo(() => {
    if (!data?.getTodosAndCategories?.todos) return 0;

    return data.getTodosAndCategories.todos.filter(todo =>
      todo.listName === 'Contact Us' &&
      todo.responders &&
      todo.responders.length > 0 &&
      todo.contactMetadata?.customerResolved === false
    ).length;
  }, [data]);

  // Count resolved conversations (contact todos where customerResolved is true)
  const resolvedConversationsCount = React.useMemo(() => {
    if (!data?.getTodosAndCategories?.todos) return 0;

    return data.getTodosAndCategories.todos.filter(todo =>
      todo.listName === 'Contact Us' &&
      todo.responders &&
      todo.responders.length > 0 &&
      todo.contactMetadata?.customerResolved === true
    ).length;
  }, [data]);

  // Update drawer state when screen size changes
  React.useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    // On mobile, close drawer after navigation
    if (isMobile) {
      setOpen(false);
    }
  };

  const mainListItems = [
    {
      text: 'Todos',
      icon: <ListAltIcon />,
      path: '/dashboard'
    },
    {
      text: 'Toduns',
      icon: <PlaylistAddCheckIcon />,
      path: '/toduns'
    },
    {
      text: 'Calendar',
      icon: <EventAvailableIcon />,
      path: '/calendar'
    },
  ];

  const secondaryListItems = [
    {
      text: 'Contact us',
      icon: <ContactSupportIcon />,
      path: '/contact'
    },
    {
      text: 'Resolved',
      icon: <CheckCircleOutlineIcon />,
      path: '/contact/resolved'
    },
    // {
    //   text: 'Wishlist',
    //   icon: <VolunteerActivismIcon />,
    //   path: '/wishlist'
    // },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" open={open}>
        <Toolbar sx={{ minHeight: isMobile ? '56px' : '64px' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{ mr: isMobile ? 1 : 2, ...(open && { display: 'none' }) }}
          >
          <MenuIcon style={{...(open && { display: 'none' }) }}/>
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }} >
            <img src={logo} alt="logo" style={{height: isMobile ? 35 : 50}} />
          </Box>
          <Typography
            variant={isMobile ? "body1" : "h6"}
            noWrap
            component="div"
            sx={{
              marginLeft: isMobile ? 0.5 : 1,
              fontSize: isMobile ? '0.9rem' : undefined
            }}
          >
            Todos
          </Typography>
        </Toolbar>
      </AppBar>
      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        <DrawerHeader>
          <Profile />
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'ltr' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>

          {mainListItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton onClick={() => handleNavigation(item.path)}>
                <ListItemIcon>
                  {mainListItems[index].icon}
                </ListItemIcon>
                <ListItemText primary={mainListItems[index].text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {secondaryListItems.map((item, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton onClick={() => handleNavigation(item.path)}>
                <ListItemIcon>
                  {item.text === 'Contact us' ? (
                    <Badge badgeContent={pendingConversationsCount} color="error">
                      {secondaryListItems[index].icon}
                    </Badge>
                  ) : item.text === 'Resolved' ? (
                    <Badge badgeContent={resolvedConversationsCount} color="success">
                      {secondaryListItems[index].icon}
                    </Badge>
                  ) : (
                    secondaryListItems[index].icon
                  )}
                </ListItemIcon>
                <ListItemText primary={secondaryListItems[index].text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          <ListItem disablePadding>
            <ListItemButton onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
              <ListItemIcon>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText primary={'logout'} />
            </ListItemButton>
          </ListItem>
        </List>
      </Drawer>
      
    </Box>
  );
}